import React, { useState, SyntheticEvent } from 'react';
import { UploadChangeParam, UploadFile } from 'antd/lib/upload/interface';
import { Spin, Button } from 'antd';
import cx from 'classnames';

import {
  downloadFile,
  getFileExt,
  getFileUploadMessages,
} from 'utils/fileUtils';
import BaseFileUpload, {
  UploadItemProps,
} from 'components/wrappers/BaseFileUpload';
import BaseAlert from 'components/wrappers/BaseAlert';
import { ApiErrorMessage } from 'constants/ErrorMessages';
import backofficeApiClient from 'api/backofficeApiClient';
import {
  getMemberLogoInfo,
  setMemberLogoInfo,
} from 'redux/reducers/recruiterSlice';
import { isAdminSelector } from 'redux/selectors/authenticationSelectors';
import { FullBOUserResponseModel } from 'api/adminApiClient';
import { getFullName } from 'utils/personUtils';
import { checkIsCompanyOwner } from 'utils/userRolesUtils';
import { useAppSelector } from 'hooks/useAppSelector';
import { useAppDispatch } from 'hooks/useAppDispatch';

import styles from './MemberLogo.module.less';

type Props = {
  url?: string;
  member?: FullBOUserResponseModel;
  companyId?: string;
  userId?: string;
};

const fileUploadMessages = getFileUploadMessages({
  fileTypes: ['JPG', 'PNG'],
});

export default function MemberLogo(props: Props) {
  const { url, member, userId, companyId } = props;
  const [imageUrl, setImageUrl] = useState(url);
  const [isLogoInfoSaving, setIsLogoInfoSaving] = useState(false);
  const isAdminRole = useAppSelector(isAdminSelector);
  const dispatch = useAppDispatch();

  const handleDownloadClick = async (event: SyntheticEvent) => {
    event.preventDefault();
    event.stopPropagation();

    if (!member) return;

    const { userId, registeredFirstName, registeredLastName, companyId } =
      member;

    if (!userId || !companyId) return;

    const result = await dispatch(getMemberLogoInfo(companyId));

    if (getMemberLogoInfo.fulfilled.match(result)) {
      const memberFullName = getFullName(
        registeredFirstName,
        registeredLastName,
      );
      const logoUrl = result.payload.logoUrl || '';
      const fileExt = getFileExt(logoUrl) || 'png';
      const fileName = `${memberFullName} company logo.${fileExt}`;

      try {
        await downloadFile({ downloadUrl: logoUrl, fileName });
      } catch (e: any) {
        BaseAlert.error(e.message);
      }
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);
    }
  };

  const handleUploaderChange = async (fileInfo: UploadChangeParam) => {
    const { fileList } = fileInfo;

    if (!fileList) return;

    const image: UploadFile = fileList[0];

    if (!image || image?.status !== 'done' || !image.response) return;

    const { getUrl, s3Key } = image.response;

    setImageUrl(getUrl);

    // save logo info
    setIsLogoInfoSaving(true);

    const result = await dispatch(
      setMemberLogoInfo({
        isAdminRole: !!isAdminRole,
        companyId: member?.companyId,
        body: {
          logoFileKey: s3Key,
        },
      }),
    );

    if (!setMemberLogoInfo.fulfilled.match(result)) {
      BaseAlert.error(result.error.message || ApiErrorMessage);
    }

    setIsLogoInfoSaving(false);
  };

  const renderDefaultImageLogo = () => (
    <div className={styles.defaultLogo}>
      <div>
        YOUR LOGO
        <div className={styles.defaultSign}>(100x100)</div>
      </div>
    </div>
  );
  const renderImageLogo = () => (
    <div
      className={styles.imgLogo}
      style={{ backgroundImage: `url(${imageUrl})` }}
    />
  );

  return (
    <>
      {checkIsCompanyOwner({ userId, companyId }) ? (
        <BaseFileUpload
          isDraggable={false}
          acceptedFileSize={2}
          acceptedFileFormat={[
            '.jpg',
            '.jpeg',
            '.png',
            'image/jpeg',
            'image/png',
          ]}
          disableOnLoading
          isUpdatedDefaultFileList={false}
          getUploadList={() => false}
          messages={fileUploadMessages}
          uploadLinkFunction={backofficeApiClient.getStorageUploadImageLink.bind(
            backofficeApiClient,
          )}
          onChange={handleUploaderChange}
        >
          {(props: UploadItemProps) => (
            <div
              className={cx(styles.logoWrapper, {
                [styles.disabled]: props.isLoading || isLogoInfoSaving,
              })}
            >
              <Spin spinning={props.isLoading || isLogoInfoSaving}>
                {!imageUrl && renderDefaultImageLogo()}
                {imageUrl && (
                  <>
                    {isAdminRole && (
                      <Button
                        className={cx('icon-btn', styles.downloadButton)}
                        size="small"
                        onClick={handleDownloadClick}
                      >
                        <i className="icon icon-download" />
                      </Button>
                    )}
                    {renderImageLogo()}
                  </>
                )}
              </Spin>
            </div>
          )}
        </BaseFileUpload>
      ) : (
        <>
          {!imageUrl && renderDefaultImageLogo()}
          {imageUrl && renderImageLogo()}
        </>
      )}
    </>
  );
}
